"use client";

import { ChevronRight, CarFront, Search, Sparkles } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const toId = (value) => String(value || "");

const getItemId = (item) =>
  item?._id ||
  item?.id ||
  item?.vehicleId ||
  item?.year ||
  item?.code ||
  item?.slug ||
  "";

const buildVariantLabel = (vehicle) => {
  const name =
    vehicle.display?.modificationGroup ||
    vehicle.technicalGroup ||
    vehicle.display?.generation ||
    vehicle.groupTitle ||
    vehicle.variantName ||
    vehicle.display?.variantName ||
    "Variant";
  const variant = vehicle.variantName || vehicle.display?.variantName || "";
  const bodyType = vehicle.bodyType || vehicle.display?.bodyType || "";
  const parts = [name];
  if (variant && String(variant).trim().toLowerCase() !== String(name).trim().toLowerCase()) {
    parts.push(variant);
  }
  if (bodyType && String(bodyType).trim().toLowerCase() !== String(name).trim().toLowerCase()) {
    parts.push(bodyType);
  }
  return parts.join(" - ");
};

const renderSelectItems = (items, labelFn = (i) => i.name, valueFn = (i) => getItemId(i)) => {
  if (!items || items.length === 0) {
    return (
      <SelectItem value="none" disabled>
        No entries found
      </SelectItem>
    );
  }

  return items.map((item) => (
    <SelectItem key={String(valueFn(item))} value={String(valueFn(item))}>
      {labelFn(item)}
    </SelectItem>
  ));
};

const renderModificationItems = (groups) => {
  if (!Array.isArray(groups) || groups.length === 0) {
    return (
      <SelectItem value="none" disabled>
        No modifications found
      </SelectItem>
    );
  }

  return groups.map((group) => (
    <div
      key={group.groupKey || `${group.groupTitle}-${group.yearRangeLabel}`}
      className="px-1 py-1"
    >
      <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80">
        {group.groupTitle} {group.yearRangeLabel ? `(${group.yearRangeLabel})` : ""}
      </div>
      {(Array.isArray(group.options) ? group.options : []).map((option) => (
        <SelectItem
          key={option.vehicleId || option._id}
          value={String(option.vehicleId || option._id)}
        >
          {option.label || buildVariantLabel(option)}
        </SelectItem>
      ))}
    </div>
  ));
};

export const HeroFinder = ({
  brands = [],
  brandId,
  modelId,
  yearId,
  variantId,
  models = [],
  years = [],
  modificationGroups = [],
  modificationOptions = [],
  loadingState,
  modelPlaceholder,
  yearPlaceholder,
  variantPlaceholder,
  isSearchEnabled,
  brandsLoading,
  onBrandChange,
  onBrandDropdownOpen,
  onModelChange,
  onYearChange,
  onVariantChange,
  onSearch,
  selection,
  onContinueShopping,
  onClearSelection,
}) => {
  const hasSelection = Boolean(selection?.brandId && selection?.modelId);

  return (
    <div className="flex flex-col justify-center">
      <div className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-blue-300 backdrop-blur">
        <Sparkles className="h-3.5 w-3.5" />
        Reliable Spare Parts & Accessories
      </div>

      <h1 className="max-w-xl text-3xl font-bold leading-[1.15] tracking-tight sm:text-4xl lg:text-5xl xl:text-6xl">
        Find the <span className="text-blue-500">Exact Part</span> for Your Car
      </h1>
      <p className="mt-6 max-w-lg text-sm leading-relaxed text-slate-300 sm:text-base">
        Professional database for car owners and garages. Get fitment-aware parts with fast dispatch across India.
      </p>

      <div className="mt-10 rounded-2xl border border-white/10 p-4 shadow-2xl backdrop-blur-xl sm:p-5 lg:max-w-2xl">
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            value={brandId}
            onValueChange={onBrandChange}
            onOpenChange={(open) => {
              if (open) onBrandDropdownOpen?.();
            }}
          >
            <SelectTrigger aria-label="Select brand" className="h-12 w-full rounded-lg border-white/20 bg-white/10 text-white shadow-none ring-0 transition-colors hover:bg-white/20 focus:border-blue-500 focus:ring-2 focus:ring-blue-400 data-[placeholder]:text-slate-200">
              <SelectValue placeholder="Brand" aria-label="Select brand" />
            </SelectTrigger>
            <SelectContent className="max-h-72 rounded-2xl border-white/10 bg-slate-900/95 text-white backdrop-blur-xl">
              {brandsLoading && brands.length === 0 ? (
                <SelectItem value="loading" disabled>
                  Loading brands...
                </SelectItem>
              ) : (
                renderSelectItems(brands)
              )}
            </SelectContent>
          </Select>

          <Select value={modelId} onValueChange={onModelChange} disabled={!brandId}>
            <SelectTrigger aria-label="Select model" className="h-12 w-full rounded-lg border-white/20 bg-white/10 text-white shadow-none ring-0 transition-colors hover:bg-white/20 focus:border-blue-500 focus:ring-2 focus:ring-blue-400 disabled:opacity-40 data-[placeholder]:text-slate-200">
              <SelectValue placeholder={modelPlaceholder} aria-label="Select model" />
            </SelectTrigger>
            <SelectContent className="max-h-72 rounded-2xl border-white/10 bg-slate-900/95 text-white backdrop-blur-xl">
              {renderSelectItems(models)}
            </SelectContent>
          </Select>

          <Select value={yearId} onValueChange={onYearChange} disabled={!modelId}>
            <SelectTrigger aria-label="Select year" className="h-12 w-full rounded-lg border-white/20 bg-white/10 text-white shadow-none ring-0 transition-colors hover:bg-white/20 focus:border-blue-500 focus:ring-2 focus:ring-blue-400 disabled:opacity-40 data-[placeholder]:text-slate-200">
              <SelectValue placeholder={yearPlaceholder} aria-label="Select year" />
            </SelectTrigger>
            <SelectContent className="max-h-72 rounded-2xl border-white/10 bg-slate-900/95 text-white backdrop-blur-xl">
              {renderSelectItems(years, (y) => y.year, (y) => getItemId(y))}
            </SelectContent>
          </Select>

          <Select value={variantId} onValueChange={onVariantChange} disabled={!yearId}>
            <SelectTrigger aria-label="Select variant" className="h-12 w-full rounded-lg border-white/20 bg-white/10 text-white shadow-none ring-0 transition-colors hover:bg-white/20 focus:border-blue-500 focus:ring-2 focus:ring-blue-400 disabled:opacity-40 data-[placeholder]:text-slate-200">
              <SelectValue placeholder={variantPlaceholder} aria-label="Select variant" />
            </SelectTrigger>
            <SelectContent className="max-h-96 rounded-2xl border-white/10 bg-slate-900/95 text-white backdrop-blur-xl">
              {renderModificationItems(modificationGroups)}
            </SelectContent>
          </Select>
        </div>

        <button
          onClick={onSearch}
          disabled={!isSearchEnabled}
          className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 font-bold text-white shadow-lg shadow-blue-500/25 transition-all hover:bg-blue-700 hover:shadow-blue-500/40 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Search className="h-4.5 w-4.5" />
          Search Parts
        </button>
      </div>

      {hasSelection ? (
        <div className="mt-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="group flex items-center gap-3 rounded-full border border-white/10 bg-white/5 p-1 pr-4 backdrop-blur-md transition-all hover:bg-white/10">
            <button
              type="button"
              onClick={onContinueShopping}
              className="flex items-center gap-3 text-left"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/20 text-blue-400 transition-all group-hover:bg-blue-500/30">
                <CarFront className="h-5 w-5" />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-bold uppercase tracking-widest text-white/40 transition-colors group-hover:text-white/60">
                  Continue Shopping
                </span>
                <span className="text-sm font-semibold text-white/90">
                  {selection.yearLabel} {selection.brandName} {selection.modelName}
                </span>
              </div>
              <ChevronRight className="h-4 w-4 text-white/30 transition-all group-hover:translate-x-1 group-hover:text-white/60" />
            </button>
            <div className="ml-auto flex items-center">
              <div className="mx-2 h-4 w-px bg-white/10" />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClearSelection?.();
                }}
                className="text-[11px] font-medium text-white/40 transition-colors hover:text-white/90"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mt-8 flex flex-wrap gap-5 text-xs font-medium text-white/50">
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
          Verified Fitment Support
        </div>
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
          Fast Dispatch Across India
        </div>
      </div>
    </div>
  );
};
